/**
 * Performance Report Pulse Templates
 * Based on best practices from Intercom, Zendesk, Freshdesk
 */

import { PulseReportTemplate } from "./types";

export const CHATBOT_PERFORMANCE: PulseReportTemplate = {
  id: "chatbot-performance",
  name: "Chatbot Performance Report",
  reportType: "performance",
  description: "Comprehensive chatbot performance metrics and insights",
  purpose:
    "Track chatbot effectiveness and identify optimization opportunities.",
  icon: "RobotOutlined",
  color: "#8b5cf6",
  suggestedHtmlTemplateId: "performance-report",
  suggestedSubject: "Your Chatbot Performance Report - {{report_period}}",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 1,
    time: "09:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: false,
    team: false,
  },
  targetAudience: "Chatbot managers and admins",
  expectedOutcomes: [
    "Clear performance visibility",
    "Actionable optimization insights",
    "ROI demonstration",
    "Continuous improvement data",
  ],
  bestPractices: [
    "Include resolution rate trends",
    "Show response time benchmarks",
    "Highlight top performing bots",
    "Provide specific improvement suggestions",
  ],
  keyMetrics: [
    "Total conversations handled",
    "Resolution rate",
    "Avg response time",
    "Customer satisfaction",
    "Escalation rate",
  ],
  tags: ["performance", "chatbot", "metrics", "kpi", "report"],
  bestFor: ["Chatbot optimization", "Performance tracking", "ROI reporting"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Complete chatbot performance dashboard",
};

export const CUSTOMER_SATISFACTION: PulseReportTemplate = {
  id: "customer-satisfaction",
  name: "Customer Satisfaction Report",
  reportType: "performance",
  description: "CSAT and NPS scores with sentiment analysis",
  purpose:
    "Monitor customer satisfaction and identify areas for improvement.",
  icon: "SmileOutlined",
  color: "#10b981",
  suggestedHtmlTemplateId: "performance-report",
  suggestedSubject: "Customer Satisfaction Report - {{satisfaction_rate}}% Happy",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 5,
    time: "15:00",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Customer success and support teams",
  expectedOutcomes: [
    "Early warning on satisfaction drops",
    "Actionable feedback insights",
    "Trend identification",
    "Team performance visibility",
  ],
  bestPractices: [
    "Track CSAT trends over time",
    "Segment by bot and topic",
    "Highlight negative feedback for review",
    "Compare to industry benchmarks",
  ],
  keyMetrics: [
    "Overall CSAT score",
    "NPS score",
    "Positive/negative ratio",
    "Score by chatbot",
    "Common complaints",
  ],
  tags: ["satisfaction", "csat", "nps", "feedback", "customer"],
  bestFor: ["Customer success teams", "Quality assurance", "Support optimization"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "CSAT and sentiment analysis dashboard",
};

export const RESOLUTION_REPORT: PulseReportTemplate = {
  id: "resolution-report",
  name: "Resolution Rate Report",
  reportType: "performance",
  description: "Track how effectively chatbots resolve customer issues",
  purpose:
    "Measure chatbot effectiveness at resolving issues without escalation.",
  icon: "CheckCircleOutlined",
  color: "#0ea5e9",
  suggestedHtmlTemplateId: "performance-report",
  suggestedSubject: "Resolution Report: {{resolution_rate}}% Issues Resolved",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 1,
    time: "10:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: false,
    team: false,
  },
  targetAudience: "Support managers and chatbot admins",
  expectedOutcomes: [
    "Clear resolution visibility",
    "Identify improvement areas",
    "Reduce support costs",
    "Optimize bot training",
  ],
  bestPractices: [
    "Break down by resolution type",
    "Show escalation reasons",
    "Track first contact resolution",
    "Compare bot vs human resolution",
  ],
  keyMetrics: [
    "Overall resolution rate",
    "First contact resolution",
    "Escalation rate",
    "Avg time to resolution",
    "Top unresolved topics",
  ],
  tags: ["resolution", "support", "efficiency", "escalation", "performance"],
  bestFor: ["Support optimization", "Bot training", "Cost reduction"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Resolution effectiveness analysis",
};

export const RESPONSE_TIME_REPORT: PulseReportTemplate = {
  id: "response-time-report",
  name: "Response Time Analysis",
  reportType: "performance",
  description: "Track and optimize chatbot response times",
  purpose:
    "Monitor response speed and identify bottlenecks affecting user experience.",
  icon: "ClockCircleOutlined",
  color: "#f59e0b",
  suggestedHtmlTemplateId: "performance-report",
  suggestedSubject: "Response Time Report - Avg {{avg_response_time}}s",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 2,
    time: "09:00",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Technical teams and performance engineers",
  expectedOutcomes: [
    "Faster response times",
    "Better user experience",
    "Performance bottleneck identification",
    "SLA compliance tracking",
  ],
  bestPractices: [
    "Set response time benchmarks",
    "Track by time of day",
    "Identify slow-performing bots",
    "Monitor against SLAs",
  ],
  keyMetrics: [
    "Average response time",
    "P95 response time",
    "Response time by bot",
    "Peak hour performance",
    "SLA compliance rate",
  ],
  tags: ["response", "time", "speed", "sla", "performance"],
  bestFor: ["Technical optimization", "SLA monitoring", "Performance tuning"],
  difficulty: "advanced",
  setupTime: "15 minutes",
  previewDescription: "Detailed response time analysis and trends",
};

export const PERFORMANCE_TEMPLATES = [
  CHATBOT_PERFORMANCE,
  CUSTOMER_SATISFACTION,
  RESOLUTION_REPORT,
  RESPONSE_TIME_REPORT,
];
